import React, { useMemo, useState } from 'react';
import { Box, CloseButton, Flex, TextV2, TooltipV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useOuterClick } from '@shared/utils/hooks/useOuterClick';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { UserConsentKey, useUserConsent } from '@shared/utils/userConsent';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { useTimeout } from '@shared/hooks/useTimeout';

const TOOLTIP_WIDTH = pxToRem(256);

interface InfoTooltipProps
  extends Readonly<{
    title: string;
    description: string;
    children: React.ReactNode;
    autoOpen?: boolean;
    userConsentKey?: UserConsentKey;
    placement?: 'top' | 'bottom' | 'left' | 'right';
  }> {}

export const InfoTooltip: React.FC<InfoTooltipProps> = ({ title, description, children, autoOpen = false, userConsentKey, placement = 'right' }) => {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const { getUserConsent, setUserConsent, loadingUserConsent } = useUserConsent();
  const isMobileOrTablet = useIsMobileOrTablet();
  const { set: setTimeout, clear: clearTimeout } = useTimeout();

  const hasSeenTooltip = userConsentKey ? getUserConsent(userConsentKey) : true;
  const shouldAutoOpen = autoOpen && !loadingUserConsent && !hasSeenTooltip;

  const handleClose = useEventCallback(() => {
    setTooltipVisible(false);
    if (userConsentKey) {
      setUserConsent(userConsentKey, true);
    }
  });

  const tooltipRef = useOuterClick(handleClose, tooltipVisible && shouldAutoOpen);

  // Auto-open tooltip on mount with a small delay to avoid jarring transitions
  React.useEffect(() => {
    if (!shouldAutoOpen) return undefined;

    setTimeout(() => {
      setTooltipVisible(true);
    }, 300);

    return () => {
      clearTimeout();
    };
  }, [shouldAutoOpen, setTimeout, clearTimeout]);

  const tooltipIsOpen = shouldAutoOpen ? tooltipVisible : undefined;

  const overrides = {
    Root: { props: { zIndex: 'overlay' } },
    Content: {
      props: {
        padding: pxToRem(24),
        borderRadius: 3,
        backgroundColor: 'newUITest1',
        color: 'white',
        width: TOOLTIP_WIDTH,
      },
    },
    Arrow: { props: { backgroundColor: 'newUITest1' } },
  };

  const tooltipContent = useMemo(
    () => (
      <Flex
        gap={3}
        flexDirection="column"
        ref={shouldAutoOpen ? tooltipRef : undefined}
      >
        {shouldAutoOpen && (
          <CloseButton
            onClick={handleClose}
            aria-label="Close tooltip"
            overrides={{
              Root: {
                props: {
                  position: 'absolute',
                  color: 'white',
                  top: pxToRem(-30),
                  right: pxToRem(-40),
                },
              },
            }}
          />
        )}
        <TextV2
          typographyVariant="label3"
          fontWeight={600}
        >
          {title}
        </TextV2>
        <Box typographyVariant="label2">{description}</Box>
      </Flex>
    ),
    [title, description, shouldAutoOpen, tooltipRef, handleClose],
  );

  return (
    <TooltipV2
      isOpen={tooltipIsOpen}
      aria-label={title}
      overrides={overrides}
      placement={isMobileOrTablet ? 'bottom' : placement}
      content={tooltipContent}
      triggerType="hover"
      onMouseEnterDelay={1000}
    >
      {children}
    </TooltipV2>
  );
};
